'use client';

import { useEffect, useState } from 'react';
import { BRAND_PREVIEW_COOKIE, parseBrandPreviewValue, type BrandPreviewId } from './shared';

function readCookie(): BrandPreviewId | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${BRAND_PREVIEW_COOKIE}=`));
  if (!match) return null;
  return parseBrandPreviewValue(decodeURIComponent(match.slice(BRAND_PREVIEW_COOKIE.length + 1)));
}

/**
 * Client-side brand preview check for components that render on public,
 * cacheable pages (e.g. the registration identity widget) where a server
 * cookie read would break caching for everyone.
 *
 * Presence-only (no signature verification in the browser): the skin it
 * unlocks is purely cosmetic strings and colours. Returns null on the first
 * render so server and client markup match, then updates after mount.
 */
export function useBrandPreview(): BrandPreviewId | null {
  const [brand, setBrand] = useState<BrandPreviewId | null>(null);
  useEffect(() => {
    setBrand(readCookie());
  }, []);
  return brand;
}

/**
 * Resolve the current Forge accent as a concrete colour value for consumers
 * that cannot use CSS variables (Mapbox markers, Stripe appearance objects,
 * canvas drawing). Falls back to Forge teal during SSR.
 */
export function resolveAccentColor(fallback = '#006D6D'): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue('--fv-accent').trim();
  return value || fallback;
}

/** resolveAccentColor as an rgba() wash at the given alpha, for consumers
 *  that need translucent accent tints as concrete values (e.g. Stripe
 *  appearance rules). Non-hex accents fall back to the teal wash. */
export function resolveAccentAlpha(alpha: number): string {
  const hex = resolveAccentColor();
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return `rgba(0, 109, 109, ${alpha})`;
  const n = parseInt(match[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}
